import React, {useState} from 'react';

import {Button, Icon, Menu, SplitButton} from '@albert-io/atomic';
import {history} from 'client/history';

import {useAssignmentReleaseScope} from './hooks/useAssignmentReleaseScope';
import ReleaseGradesModal from './ReleaseGradesModal.react';

interface AssignmentGradeSplitButtonProps {
  assignmentId: string;
  assignmentName: string;
  /** Absent on All Classwork: the grading view resolves its own class and a release spans them all. */
  classroomId?: string;
  classroomName?: string;
  /** FRQ-response counts for the scope above; drive the two-state primary. */
  gradedCount: number;
  totalCount: number;
  /** Graded but not yet released to this class; informs the modal's summary. */
  releasableCount: number;
  /** Set when masquerading, so the scope query reads the same educator's index as the list. */
  asEducatorId?: string;
}

/**
 * Two-state grading affordance on an assignment row: grade-first ("Grade" + "Release Grades" menu)
 * until every response is graded, then release-first ("Release Grades" + "Edit Grades" menu). Meant
 * to sit inside the row's action cluster, which already stops row-click propagation. Without a
 * `classroomId` (All Classwork) both actions span every class the teacher teaches.
 */
export const AssignmentGradeSplitButton = ({
  assignmentId,
  assignmentName,
  classroomId,
  classroomName,
  gradedCount,
  totalCount,
  releasableCount,
  asEducatorId
}: AssignmentGradeSplitButtonProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {allClassesCounts, classroomNames, spansMultipleClasses, isScopeLoading, hasScopeError} =
    useAssignmentReleaseScope({
      assignmentId,
      asEducatorId,
      skip: !isModalOpen
    });

  // Same count the modal shows: published alone reads 0 until the release sweep runs, which would
  // leave a fully graded row offering "Grade" while its own modal reports it finished.
  const allGraded = totalCount > 0 && gradedCount + releasableCount >= totalCount;

  const goToGrading = () =>
    history.pushState(
      null,
      classroomId
        ? `/classes/${classroomId}/assignments/${assignmentId}/student-detail`
        : `/assignments/${assignmentId}/student-detail`
    );
  const openModal = () => setIsModalOpen(true);

  return (
    <>
      <SplitButton>
        <Button
          size='s'
          color='primary'
          variant='outlined'
          onClick={allGraded ? openModal : goToGrading}
          data-testid={`grade-${assignmentId}`}
        >
          {allGraded ? 'Release Grades' : 'Grade'}
        </Button>
        <Menu closeOnItemClick position='bottom-end' positionFixed>
          <Menu.Trigger>
            {(triggerProps) => (
              <Button
                {...triggerProps}
                size='s'
                color='primary'
                variant='outlined'
                aria-label='More grade actions'
                data-testid={`grade-actions-${assignmentId}`}
              >
                <Icon icon='caret-down' iconStyle='solid' />
              </Button>
            )}
          </Menu.Trigger>
          {/* Only the inner menu is sized; a narrowed wrapper would let it overflow rightward. */}
          <Menu.Content className='[&>.o-menu-content]:w-[174px] [&>.o-menu-content]:min-w-[174px]'>
            {allGraded ? (
              <Menu.Item leftIconProps={{icon: 'pen-to-square'}} onClick={goToGrading}>
                Edit Grades
              </Menu.Item>
            ) : (
              <Menu.Item leftIconProps={{icon: 'paper-plane'}} onClick={openModal}>
                Release Grades
              </Menu.Item>
            )}
          </Menu.Content>
        </Menu>
      </SplitButton>
      {isModalOpen ? (
        <ReleaseGradesModal
          assignmentId={assignmentId}
          assignmentName={assignmentName}
          classroomId={classroomId}
          classroomName={classroomName}
          gradedCount={gradedCount}
          totalCount={totalCount}
          releasableCount={releasableCount}
          spansMultipleClasses={spansMultipleClasses}
          classroomNames={classroomNames}
          allClassesCounts={allClassesCounts}
          isScopeLoading={isScopeLoading}
          hasScopeError={hasScopeError}
          onClose={() => setIsModalOpen(false)}
        />
      ) : null}
    </>
  );
};

export default AssignmentGradeSplitButton;
